import { Icon, Pagination, Fx, Input } from 'Components';
import { useEffect, useMemo, useState } from 'react';
import { Select, Spin } from 'antd';
import moment from 'moment';
import { debounce } from 'lodash';
import { CampaignThresholdData } from '../..';
import TemplateCard from '../CampaignSteps/components/TemplateCard';
import {
	DEFAULT_TEMPLATES_PAGESIZE,
	DEFAULT_WHITELIST_URL
} from '../../PhishingManager.constants';
import ThreeMonthWhitelistingModal from '../CampaignSteps/components/ThreeMonthWhitelistingModal';
import FirstWhitelistingModal from '../CampaignSteps/components/FirstWhitelistingModal';
import { FilterResults } from '../CampaignSteps';
import { getPhishingCampaignTemplates } from '../../service/index.Service';
import { capitalize, errorMessageHandler } from '../../utils';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';

type Step1Props = {
	selectedTemplate: string | number | null;
	tenantId: string | number | undefined;
	onSelect: (value: string | number | null) => void;
	onNext: () => void;
	onClose: () => void;
	thresholdData: null | CampaignThresholdData;
	filterResults: FilterResults | null;
	step1State: {
		search: string;
		language: string;
		category: string;
		difficulty: string;
		templatePage: number;
	};
	setStep1State: React.Dispatch<React.SetStateAction<any>>;
	setToast: (
		toast: { message: string; type: 'warning' | 'success' } | null
	) => void;
	isMobile: boolean;
};

interface DropdownProps {
	value: string | number;
	label: string;
}

export interface Template {
	value: string | number;
	label: string;
	categories: string[];
	languages: string[];
	difficulty: string;
	img: string;
}

interface ApiTemplate {
	id: number;
	name: string;
	img_path: string;
	difficulties: string;
	languages: number[];
	categories: number[];
}

export default function Step1({
	selectedTemplate,
	filterResults,
	onSelect,
	onNext,
	setToast,
	thresholdData,
	step1State,
	setStep1State,
	tenantId,
	isMobile
}: Step1Props) {
	const [languages, setLanguages] = useState<DropdownProps[]>([]);
	const [categories, setCategories] = useState<DropdownProps[]>([]);
	const [difficulties, setDifficulties] = useState<DropdownProps[]>([]);
	const { search, language, category, difficulty, templatePage } = step1State;
	const pageSize = DEFAULT_TEMPLATES_PAGESIZE;
	const [
		openFirstWhitelistingModal,
		setOpenFirstWhitelistingModal
	] = useState(false);
	const [
		openThreeMonthWhitelistingModal,
		setOpenThreeMonthWhitelistingModal
	] = useState(false);
	const [hasShownWhitelisting, setHasShownWhitelisting] = useState(false);
	const [templates, setTemplates] = useState<Template[]>([]);
	const [totalTemplates, setTotalTemplates] = useState(0);
	const [loading, setLoading] = useState(false);
	const [debouncedSearch, setDebouncedSearch] = useState('');

	const debouncedSetSearch = useMemo(
		() =>
			debounce((value: string) => {
				setDebouncedSearch(value);
			}, 500),
		[]
	);

	const handleCloseFirstModal = () => {
		setOpenFirstWhitelistingModal(false);
	};

	const handleConfirmFirstModal = () => {
		window.open(DEFAULT_WHITELIST_URL, '_blank', 'noopener,noreferrer');
		setOpenFirstWhitelistingModal(false);
	};

	const fetchCampaignsTemplates = async () => {
		try {
			const hasFilters =
				!!difficulty ||
				(selectedLanguageIds?.length ?? 0) > 0 ||
				(selectedCategoryIds?.length ?? 0) > 0;
			if (search && search.length <= 2 && !hasFilters) {
				return;
			}
			setLoading(true);
			const response = await getPhishingCampaignTemplates(tenantId, {
				name: search && search.length > 2 ? search : undefined,
				difficulties: difficulty ? [difficulty] : undefined,
				languages: selectedLanguageIds,
				categories: selectedCategoryIds,
				page: templatePage,
				limit: pageSize
			});

			const mappedTemplates: Template[] = response.data.map(
				(el: ApiTemplate) => ({
					value: el.id,
					label: el.name,
					categories: el.categories,
					languages: el.languages,
					difficulty: capitalize(el.difficulties),
					img: el.img_path
				})
			);

			setTemplates((prev) => {
				if (!isMobile || templatePage === 1) {
					return mappedTemplates;
				}
				return [...prev, ...mappedTemplates];
			});

			setTotalTemplates(response.meta.total);
		} catch (err: any) {
			const errorMessage = errorMessageHandler(
				err,
				'Failed to create campaign step 2'
			);
			setToast({
				message: errorMessage || 'Failed to create campaign step 2',
				type: 'warning'
			});
			setTemplates([]);
			setTotalTemplates(0);
		} finally {
			setLoading(false);
		}
	};

	const handleCloseThreeMonthModal = () => {
		setOpenThreeMonthWhitelistingModal(false);
	};

	const shouldShowThreeMonthModal = (firstCampaignDate: string) => {
		const start = moment(firstCampaignDate);
		const now = moment();
		if (now.isBefore(start.clone().add(3, 'months'), 'day')) {
			return false;
		}
		const cyclesPassed = Math.floor(now.diff(start, 'months', true) / 3);
		const lastShownCycle = Number(
			localStorage.getItem(`${tenantId}_whitelist_last_cycle_shown`) ?? -1
		);
		return cyclesPassed > lastShownCycle;
	};

	const handleConfirmThreeMonthModal = () => {
		const start = moment(thresholdData?.first_campaign_created_at);
		const now = moment();
		const cyclesPassed = Math.floor(now.diff(start, 'months', true) / 3);
		localStorage.setItem(
			`${tenantId}_whitelist_last_cycle_shown`,
			String(cyclesPassed)
		);
		window.open(DEFAULT_WHITELIST_URL, '_blank', 'noopener,noreferrer');
		setOpenThreeMonthWhitelistingModal(false);
	};

	const dropdownIcon = (
		<div className="cs-dropdownWrapper">
			<Icon type="campaignSetupDropdown" />
		</div>
	);

	const selectedLanguageIds = filterResults?.languages
		?.filter((l) => l.name === language)
		.map((l) => l.id);

	const selectedCategoryIds = filterResults?.categories
		?.filter((c) => c.name === category)
		.map((c) => c.id);

	useEffect(() => {
		if (!!filterResults) {
			setLanguages([
				{ value: '', label: 'Select a language' },
				...filterResults?.languages?.map(
					(el: { id: number; name: string }) => {
						return { value: el.name, label: el.name };
					}
				)
			]);
			setDifficulties([
				{ value: '', label: 'Select a difficulty' },
				...(filterResults?.difficulties ?? []).map((el: string) => {
					const formatted =
						el.charAt(0).toUpperCase() + el.slice(1).toLowerCase();

					return {
						value: formatted,
						label: formatted
					};
				})
			]);
			setCategories([
				{ value: '', label: 'Select a category' },
				...filterResults?.categories.map(
					(el: { id: number; name: string }) => {
						return { value: el.name, label: el.name };
					}
				)
			]);
		}
	}, [filterResults]);

	useEffect(() => {
		if (!thresholdData || hasShownWhitelisting) return;
		const { first_campaign_created_at } = thresholdData;
		setOpenFirstWhitelistingModal(false);
		setOpenThreeMonthWhitelistingModal(false);
		if (!first_campaign_created_at) {
			setOpenFirstWhitelistingModal(true);
			setHasShownWhitelisting(true);
			return;
		}
		if (
			thresholdData?.first_campaign_created_at &&
			shouldShowThreeMonthModal(thresholdData.first_campaign_created_at)
		) {
			setOpenThreeMonthWhitelistingModal(true);
		}
	}, [thresholdData, hasShownWhitelisting]);

	useEffect(() => {
		setTemplates([]);
		setStep1State((prev: any) => ({ ...prev, templatePage: 1 }));
	}, [debouncedSearch, category, difficulty, language]);

	useEffect(() => {
		fetchCampaignsTemplates();
	}, [debouncedSearch, category, difficulty, language, templatePage]);

	useEffect(() => {
		return () => {
			debouncedSetSearch.cancel();
		};
	}, [debouncedSetSearch]);

	return (
		<Spin spinning={loading}>
			<div className="campaign-template-list step-card">
				<div className="card-header">
					<div className="card-title">Scenario</div>
					<div className="card-subtitle">
						Choose the scenario for the campaign
					</div>
				</div>

				<div className="campaign-filter-section">
					<Input.Search
						placeholder="Search"
						value={search}
						allowClear
						onChange={(e) => {
							const value = e.target.value;
							setStep1State((prev: any) => ({
								...prev,
								search: value
							}));
							debouncedSetSearch(value);
						}}
						suffix={<Icon type="search" />}
						className="campaign-setup-search"
					/>

					<Select
						className="campaign-setup-select"
						dropdownClassName="campaign-setup-filter-option-select"
						suffixIcon={dropdownIcon}
						value={language}
						onChange={(value) =>
							setStep1State((prev: any) => ({
								...prev,
								language: value
							}))
						}
						options={languages}
						getPopupContainer={(trigger) => trigger.parentElement}
					/>

					<Select
						className="campaign-setup-select"
						dropdownClassName="campaign-setup-filter-option-select"
						suffixIcon={dropdownIcon}
						value={category}
						onChange={(value) =>
							setStep1State((prev: any) => ({
								...prev,
								category: value
							}))
						}
						options={categories}
						getPopupContainer={(trigger) => trigger.parentElement}
					/>

					<Select
						className="campaign-setup-select"
						dropdownClassName="campaign-setup-filter-option-select"
						suffixIcon={dropdownIcon}
						value={difficulty}
						onChange={(value) =>
							setStep1State((prev: any) => ({
								...prev,
								difficulty: value
							}))
						}
						options={difficulties}
						getPopupContainer={(trigger) => trigger.parentElement}
					/>
				</div>

				<div className="template-list-section">
					<div className="cs-template-list-header">
						Select template ({totalTemplates} results)
					</div>

					{templates.length === 0 && !loading ? (
						<div className="cs-empty-state">
							No templates match your filters.
						</div>
					) : isMobile ? (
						<InfinityScrollWrapper
							dataLength={templates.length}
							hasMore={templates.length < totalTemplates}
							onLoadMore={() => {
								setStep1State((prev: any) => ({
									...prev,
									templatePage: prev.templatePage + 1
								}));
							}}
							height={600}
							scrollableId="template-scroll-container"
							loaderText={'Loading more templates...'}
							endMessageText={'All templates loaded'}
						>
							<Cards
								templates={templates}
								selectedTemplate={selectedTemplate}
								onSelect={onSelect}
							/>
						</InfinityScrollWrapper>
					) : (
						<Cards
							templates={templates}
							selectedTemplate={selectedTemplate}
							onSelect={onSelect}
						/>
					)}
					{totalTemplates > pageSize && !isMobile && (
						<Fx shrink={0} fullWidth justify="center">
							<Pagination
								total={totalTemplates}
								pageSize={pageSize}
								current={templatePage}
								onChange={(page) =>
									setStep1State((prev: any) => ({
										...prev,
										templatePage: page
									}))
								}
								showSizeChanger={false}
								hideOnSinglePage={true}
								responsive={true}
							/>
						</Fx>
					)}
				</div>

				<FirstWhitelistingModal
					open={openFirstWhitelistingModal}
					onClose={handleCloseFirstModal}
					onConfirm={handleConfirmFirstModal}
				/>
				<ThreeMonthWhitelistingModal
					open={openThreeMonthWhitelistingModal}
					onClose={handleCloseThreeMonthModal}
					onConfirm={handleConfirmThreeMonthModal}
				/>
			</div>
		</Spin>
	);
}

const Cards = ({
	templates,
	onSelect,
	selectedTemplate
}: {
	templates: Template[];
	onSelect: (value: string | number | null) => void;
	selectedTemplate: string | number | null;
}) => {
	return (
		<div className="cs-template-list-section">
			{templates.map((t) => (
				<TemplateCard
					key={t.value}
					template={t}
					selected={selectedTemplate === t.value}
					onSelect={onSelect}
				/>
			))}
		</div>
	);
};
